"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import type { Guide } from "@/lib/config/guides/types";
import { allGuides } from "@/lib/config/guides/guides";
import { allGuidesEn } from "@/lib/config/guides/guides-en";

const FEATURED_SLUGS_SV = [
  "vad-ar-rut-avdrag",
  "kalkyl-rut-rot",
  "ungdomskraft-vs-tiptapp",
  "rut-avdrag-bortforsling",
];

const FEATURED_SLUGS_EN = [
  "what-is-rut-deduction",
  "rut-rot-calculator",
  "ungdomskraft-vs-tiptapp",
  "rut-deduction-junk-removal",
];

function getGuides(locale: "sv" | "en"): Guide[] {
  const slugs = locale === "en" ? FEATURED_SLUGS_EN : FEATURED_SLUGS_SV;
  const source = locale === "en" ? allGuidesEn : allGuides;
  const map = new Map(source.map((g) => [g.slug, g]));
  return slugs.map((s) => map.get(s)).filter(Boolean) as Guide[];
}

export default function GuidePreviewSection({
  locale = "sv",
}: {
  locale?: "sv" | "en";
}) {
  const guides = getGuides(locale);
  const prefix = locale === "en" ? "/en/guide" : "/guide";
  const heading =
    locale === "en" ? "Tips from Ungdomskraft" : "Tips från Ungdomskraft";

  if (guides.length === 0) return null;

  return (
    <section className="py-10 sm:py-16 bg-white">
      <div className="mx-auto max-w-screen-2xl px-6 xl:px-14">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-brand-dark mb-8 sm:mb-12"
        >
          {heading}
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {guides.map((guide) => (
            <a
              key={guide.slug}
              href={`${prefix}/${guide.slug}`}
              className="group flex flex-col rounded-2xl bg-white transition-all duration-300 hover:-translate-y-1"
            >
              {/* Cover image */}
              <div className="relative aspect-[16/9] overflow-hidden rounded-2xl">
                {guide.coverImage ? (
                  <Image
                    src={guide.coverImage}
                    alt={guide.coverImageAlt}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-brand-primary to-[#1d3547]" />
                )}
              </div>

              {/* Body */}
              <div className="flex flex-1 flex-col pt-4">
                {/* Tags */}
                <div className="mb-2.5 flex flex-wrap gap-1.5">
                  {guide.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-500"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <h3 className="text-[15px] sm:text-base font-bold text-brand-dark line-clamp-2 leading-snug font-heading">
                  {guide.title}
                </h3>
                <p className="mt-1.5 text-[13px] text-gray-500 line-clamp-2 leading-relaxed">
                  {guide.excerpt}
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
